/**
 * Changelog Page Component
 *
 * Timeline layout: latest version gets a hero card (full width),
 * older versions stack in a compact vertical timeline.
 */

import React, { useState } from 'react';
import { ArrowLeft, Sparkles, Wrench, Bug, Calendar, Tag, ChevronDown, ChevronRight } from 'lucide-react';
import { changelog, CURRENT_VERSION } from '@/data/changelog';
import type { ChangelogEntry } from '@/data/changelog';

interface ChangelogPageProps {
  onBack: () => void;
}

/** Shared detail body — used by hero card and expanded timeline cards */
const VersionDetailBody: React.FC<{ entry: ChangelogEntry }> = ({ entry }) => (
  <div className="px-6 py-5">
    {/* Highlights */}
    {entry.highlights.length > 0 && (
      <ul className="space-y-1.5 mb-5">
        {entry.highlights.map((highlight, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
            <span className="text-blue-500 mt-0.5">&rarr;</span>
            <span>{highlight}</span>
          </li>
        ))}
      </ul>
    )}

    {/* Features in 2-col grid */}
    {entry.features && entry.features.length > 0 && (
      <div className="mb-5">
        <h4 className="flex items-center gap-2 text-xs font-semibold text-gray-900 dark:text-white mb-3 uppercase tracking-wide">
          <Sparkles className="w-3.5 h-3.5 text-blue-500" />
          Нови функции
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {entry.features.map((feature, i) => (
            <div key={i} className="flex items-start gap-3 p-3 bg-blue-50/50 dark:bg-blue-900/10 rounded-lg">
              {feature.icon && <span className="text-lg flex-shrink-0">{feature.icon}</span>}
              <div className="min-w-0">
                <span className="font-medium text-gray-900 dark:text-white text-sm block">
                  {feature.title}
                </span>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Improvements + Bugfixes side by side */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      {entry.improvements && entry.improvements.length > 0 && (
        <div>
          <h4 className="flex items-center gap-2 text-xs font-semibold text-gray-900 dark:text-white mb-2 uppercase tracking-wide">
            <Wrench className="w-3.5 h-3.5 text-green-500" />
            Подобрения
          </h4>
          <ul className="space-y-1">
            {entry.improvements.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                <span className="text-green-500 mt-0.5">+</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {entry.bugfixes && entry.bugfixes.length > 0 && (
        <div>
          <h4 className="flex items-center gap-2 text-xs font-semibold text-gray-900 dark:text-white mb-2 uppercase tracking-wide">
            <Bug className="w-3.5 h-3.5 text-orange-500" />
            Поправки
          </h4>
          <ul className="space-y-1">
            {entry.bugfixes.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                <span className="text-orange-500 mt-0.5">~</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  </div>
);

const HeroVersionCard: React.FC<{ entry: ChangelogEntry }> = ({ entry }) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-blue-300 dark:border-blue-700 ring-2 ring-blue-100 dark:ring-blue-900/50 overflow-hidden">
      {/* Hero header */}
      <div className="bg-gradient-to-r from-blue-500 via-blue-600 to-purple-600 px-6 py-6 text-white">
        <div className="flex items-center gap-3 mb-3">
          <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-semibold">
            v{entry.version}
          </span>
          <span className="px-2 py-0.5 bg-green-400/30 text-green-100 text-xs rounded-full font-medium">
            Текуща
          </span>
        </div>
        <h2 className="text-2xl font-bold mb-1">{entry.title}</h2>
        <span className="flex items-center gap-1.5 text-sm text-white/70">
          <Calendar className="w-3.5 h-3.5" />
          {new Date(entry.date).toLocaleDateString('bg-BG', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </span>
      </div>

      <VersionDetailBody entry={entry} />
    </div>
  );
};

const TimelineVersionCard: React.FC<{ entry: ChangelogEntry; isExpanded: boolean; onToggle: () => void }> = ({ entry, isExpanded, onToggle }) => {
  const hasDetails = (entry.features && entry.features.length > 0) ||
    (entry.improvements && entry.improvements.length > 0) ||
    (entry.bugfixes && entry.bugfixes.length > 0);

  return (
    <div className="relative pl-8">
      {/* Timeline dot */}
      <div className={`absolute left-0 top-1 w-3 h-3 rounded-full ring-4 ring-gray-50 dark:ring-gray-900 transition-colors ${isExpanded ? 'bg-blue-500 dark:bg-blue-400' : 'bg-gray-300 dark:bg-gray-600'}`} />

      {/* Card */}
      <div className={`bg-white dark:bg-gray-800 rounded-lg border overflow-hidden transition-colors ${isExpanded ? 'border-blue-200 dark:border-blue-800' : 'border-gray-200 dark:border-gray-700'}`}>
        {/* Header row — clickable */}
        <button
          onClick={hasDetails ? onToggle : undefined}
          className={`w-full flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700/50 text-left ${hasDetails ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/50' : 'cursor-default'}`}
        >
          {hasDetails && (
            isExpanded
              ? <ChevronDown className="w-4 h-4 text-blue-500 flex-shrink-0" />
              : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
          )}
          {!hasDetails && (
            <div className="p-1 rounded bg-gray-200 dark:bg-gray-700">
              <Tag className="w-3 h-3 text-gray-500 dark:text-gray-400" />
            </div>
          )}
          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
            v{entry.version}
          </span>
          <span className="text-lg font-semibold text-gray-900 dark:text-white flex-1 truncate">
            {entry.title}
          </span>
          <span className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
            <Calendar className="w-3 h-3" />
            {new Date(entry.date).toLocaleDateString('bg-BG', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </span>
        </button>

        {isExpanded ? (
          /* Full detail view */
          <VersionDetailBody entry={entry} />
        ) : (
          /* Compact summary */
          <div className="px-4 py-3 space-y-3">
            {/* Highlights as inline tags */}
            {entry.highlights.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {entry.highlights.map((h, i) => (
                  <span key={i} className="inline-flex items-center px-2 py-0.5 bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 rounded text-xs">
                    {h}
                  </span>
                ))}
              </div>
            )}

            {/* Features compact list */}
            {entry.features && entry.features.length > 0 && (
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {entry.features.map((f, i) => (
                  <span key={i} className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
                    {f.icon && <span className="text-sm">{f.icon}</span>}
                    <span className="font-medium">{f.title}</span>
                  </span>
                ))}
              </div>
            )}

            {/* Improvements + bugfixes counts */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
              {entry.improvements && entry.improvements.length > 0 && (
                <span className="flex items-center gap-1">
                  <Wrench className="w-3 h-3 text-green-500" />
                  {entry.improvements.length} подобрени{entry.improvements.length === 1 ? 'е' : 'я'}
                </span>
              )}
              {entry.bugfixes && entry.bugfixes.length > 0 && (
                <span className="flex items-center gap-1">
                  <Bug className="w-3 h-3 text-orange-500" />
                  {entry.bugfixes.length} поправк{entry.bugfixes.length === 1 ? 'а' : 'и'}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const ChangelogPage: React.FC<ChangelogPageProps> = ({ onBack }) => {
  const [latestEntry, ...olderEntries] = changelog;
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set());

  const toggleVersion = (version: string) => {
    setExpandedVersions((prev) => {
      const next = new Set(prev);
      if (next.has(version)) {
        next.delete(version);
      } else {
        next.add(version);
      }
      return next;
    });
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="flex items-center gap-4 px-6 py-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={onBack}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          title="Назад"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        </button>
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
            История на версиите
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Текуща версия: {CURRENT_VERSION}
          </p>
        </div>
      </div>

      {/* Content - Timeline */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Latest version - hero card */}
          {latestEntry && <HeroVersionCard entry={latestEntry} />}

          {/* Older versions - expandable timeline */}
          {olderEntries.length > 0 && (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-[5px] top-4 bottom-4 w-0.5 bg-gray-200 dark:bg-gray-700" />

              <div className="space-y-4">
                {olderEntries.map((entry) => (
                  <TimelineVersionCard
                    key={entry.version}
                    entry={entry}
                    isExpanded={expandedVersions.has(entry.version)}
                    onToggle={() => toggleVersion(entry.version)}
                  />
                ))}
              </div>
            </div>
          )}

          {changelog.length === 0 && (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              Няма налична история на версиите.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangelogPage;
